/**
 * Top-level decisions for a bot's turn: which action to take (deathmatch
 * flat scoring) and what to do after firing. Objective modes plan through
 * botBehaviorGraphs instead and use only the post-fire choice from here.
 */

import { knownEnemies } from './targeting';
import { standingWithJob } from '../../core/body';
import { coverAround } from '../../core/cover';
import type { PlayerState, GameState, BotConfig, TransitGraph, BotFogContext } from '../../types';
import type { BotAction, BotPostFireAction, BotExposureContext } from './types';
import { vecNormalize, vecDot } from '../../math';
import { BUILD_MOVEMENT_COST, FIRE_MOVEMENT_COST, JUMP_MIN_COST } from '../../constants';
import { canBuildEcotourism } from '../../core/economy';
import { getPlayerCarrying } from '../../core/carryables';
import type { SeededRandom } from '../../core/random';
import { scoreModeActions, getBotObjectiveTarget, getBotSamePlanetObjectiveDir, getObjectiveJumpBoost } from '../botModeAI';
import { hasAmmo, hasAnyAmmo, canBotHeal, getAliveEnemies } from './common';
import { assessAmmoNeeds } from './economy';
import { tuningFor } from './tuning';

// =============================================================================
// ACTION SELECTION
// =============================================================================

/**
 * Choose which action the bot takes this turn (flat scoring).
 * Used for deathmatch; non-deathmatch modes use planBotTurn() behavior graphs.
 * Personality drives the scoring, skill adds noise to suboptimal choices.
 */
export function chooseBotAction(
  player: PlayerState,
  gameState: GameState,
  config: BotConfig,
  rng: SeededRandom,
  transitGraph?: TransitGraph,
  fogContext?: BotFogContext
): BotAction {
  const { personality, skill } = config;
  const tuning = tuningFor(config);
  const t = tuning.action;
  const enemies = getAliveEnemies(player, gameState.players);
  if (enemies.length === 0) return 'skip';

  // Command override: check pendingCommand first
  const cmd = player.pendingCommand;
  if (cmd) {
    if ((cmd.type === 'fireAtTarget' || cmd.type === 'fireAtLocation' || cmd.type === 'fireProbe')
        && !player.holdFire && hasAnyAmmo(player) && player.movementRemaining >= FIRE_MOVEMENT_COST) {
      return 'fire';
    }
    if (cmd.type === 'moveToPlanet' && cmd.targetPlanetIndex !== undefined
        && cmd.targetPlanetIndex !== player.planetIndex
        && player.movementRemaining >= JUMP_MIN_COST) {
      return 'jump';
    }
    if (cmd.type === 'holdPosition' && cmd.surfaceDir) {
      const currentDir = vecNormalize(player.surfaceDir);
      const targetDir = vecNormalize(cmd.surfaceDir);
      const dot = vecDot(currentDir, targetDir);
      const atPosition = dot > 0.995; // close enough to target

      if (cmd.holdPreset === 'advance' || cmd.holdPreset === 'waypoint') {
        // Advance/waypoint: fire first, then move toward target
        if (!player.holdFire && hasAnyAmmo(player) && player.movementRemaining >= FIRE_MOVEMENT_COST) {
          return 'fire';
        }
        // holdFire or can't fire: just move
        if (!atPosition && player.movementRemaining >= 0.5) {
          return 'move';
        }
        return 'skip';
      }

      // Cover/hold/waypoint: walk to position first, then fire
      if (!atPosition && player.movementRemaining >= 0.5) {
        return 'move';
      }
      // At position — fire if possible (unless holdFire)
      if (!player.holdFire && hasAnyAmmo(player) && player.movementRemaining >= FIRE_MOVEMENT_COST) {
        return 'fire';
      }
      return 'skip';
    }
    // prioritizeEnemy: no action override, falls through to normal scoring
  }

  const scores: Record<BotAction, number> = {
    fire: 0,
    move: 0,
    jump: 0,
    build: 0,
    shop: 0,
    skip: 0,
    heal: -Infinity,
    plantStake: -Infinity,
    removeStake: -Infinity,
    interact: -Infinity,
    collect: -Infinity,
    deliver: -Infinity,
    buildDrill: -Infinity,
    pickup: -Infinity,
    claim: -Infinity,
  };

  // -- Fire --
  scores.fire = t.fireBase + t.fireAggression * personality.aggression;
  if (hasAnyAmmo(player)) scores.fire += t.fireAmmoBonus;
  else scores.fire = -Infinity;
  if (player.movementRemaining < FIRE_MOVEMENT_COST) scores.fire = -Infinity;
  if (player.holdFire) scores.fire = -Infinity;

  // -- Move --
  scores.move = t.moveBase + t.moveMobility * personality.mobility;
  // K3: an enemy *known* to be on this planet — 37.20 ledger #6
  const enemyOnSamePlanet = knownEnemies(player, gameState, fogContext).some(e => e.planetIndex === player.planetIndex);
  if (enemyOnSamePlanet) scores.move += t.moveEnemySamePlanetBonus;
  if (player.health < t.moveLowHealthBelow) scores.move += t.moveLowHealthBonus;
  if (player.movementRemaining < 0.5) scores.move = -Infinity;

  // -- Jump --
  scores.jump = t.jumpBase + t.jumpMobility * personality.mobility + t.jumpRisk * personality.riskTolerance;
  if (player.movementRemaining < JUMP_MIN_COST) scores.jump = -Infinity;
  // Multi-hop boost: skilled bots prefer jumping when graph shows a route
  if (transitGraph && skill >= t.jumpGraphMinSkill && scores.jump > -Infinity) {
    const entries = transitGraph.get(player.planetIndex);
    if (entries && entries.length > 0) {
      scores.jump += t.jumpGraphBonus;
    }
  }
  // Fuel boost: bots with fuel are more inclined to jump
  if (player.jumppackFuel > 0 && scores.jump > -Infinity) {
    scores.jump += t.jumpFuelBonus;
  }
  // Mode objective boost: bots jump toward objective planets
  if (scores.jump > -Infinity) {
    const objBoost = getObjectiveJumpBoost(player, gameState, skill, personality);
    if (objBoost > 0) scores.jump += objBoost;
  }

  // -- Build --
  scores.build = t.buildBase - t.buildAggressionPenalty * personality.aggression - t.buildRiskPenalty * personality.riskTolerance;
  const canBuild = canBuildEcotourism(player.id, gameState.structures)
    && player.movementRemaining >= BUILD_MOVEMENT_COST
    && player.cbp >= t.buildMinCbp;
  if (!canBuild) scores.build = -Infinity;
  else {
    const hasBuilding = gameState.structures.some(s => s.ownerId === player.id && standingWithJob(s, 'income'));   // X18: the one that pays
    if (!hasBuilding) scores.build += t.buildFirstBonus;
  }

  // -- Shop --
  const needs = assessAmmoNeeds(player, personality, rng, undefined, tuning);
  scores.shop = -Infinity;
  if (needs.needsAmmo && player.cbp >= t.shopMinCbp) {
    scores.shop = t.shopBase + t.shopUrgency * needs.urgency;
  }

  // -- Skip --
  scores.skip = t.skip;

  // -- Heal --
  if (canBotHeal(player)) {
    // Higher score when health is lower; risk-averse bots heal more eagerly
    const healthFraction = player.health / 100;
    scores.heal = t.healBase + (1 - healthFraction) * t.healMissingHealth + (1 - personality.riskTolerance) * t.healCaution;
    // Critically low health: strong heal preference
    if (player.health <= t.healCriticalBelow) scores.heal += t.healCriticalBonus;
  }

  // Fog awareness: when no enemies are directly visible, prefer scouting with probes
  if (fogContext && fogContext.visibleEnemyIds.length === 0 && hasAmmo(player, 'probe')
      && player.movementRemaining >= FIRE_MOVEMENT_COST && scores.fire > -Infinity) {
    scores.fire += t.probeWhenBlindBonus; // boost fire (which may become a probe via shouldScoutProbe)
  }

  // Mode-specific action scoring (live personality, so adaptive shifts apply)
  const modeScores = scoreModeActions(player, gameState, gameState.config.modeConfig, rng, fogContext, personality);
  for (const [action, score] of Object.entries(modeScores)) {
    const key = action as BotAction;
    scores[key] = Math.max(scores[key] ?? -Infinity, score);
  }

  // Add noise based on inverse skill (low skill = more random choices)
  const noise = (1 - skill) * t.noise;
  for (const key of Object.keys(scores) as BotAction[]) {
    if (scores[key] > -Infinity) {
      scores[key] += (rng.next() - 0.5) * noise;
    }
  }

  // Pick highest-scoring action
  let best: BotAction = 'fire';
  let bestScore = -Infinity;
  for (const key of Object.keys(scores) as BotAction[]) {
    if (scores[key] > bestScore) {
      bestScore = scores[key];
      best = key;
    }
  }

  return best;
}

// =============================================================================
// POST-FIRE ACTION SELECTION
// =============================================================================

/**
 * Decide what to do after firing: move, jump, or skip.
 * Exposure-aware: spotted bots strongly prefer repositioning.
 */
export function chooseBotPostFireAction(
  player: PlayerState,
  gameState: GameState,
  config: BotConfig,
  rng: SeededRandom,
  exposure: BotExposureContext,
  fogContext?: BotFogContext
): BotPostFireAction {
  // holdPosition: suppress post-fire movement — stay put (except advance/waypoint: move toward target after firing)
  if (player.pendingCommand?.type === 'holdPosition') {
    if (player.pendingCommand.holdPreset === 'advance' || player.pendingCommand.holdPreset === 'waypoint') {
      return 'move'; // advance/waypoint: fire then move toward target
    }
    return 'skip';
  }

  const { personality, skill } = config;
  const t = tuningFor(config).postFire;
  const spotted = exposure.isSpotted ? 1 : 0;

  // 37.18c: a sheltered unit with nobody on its planet holds its hole
  const planetHere = gameState.planets[player.planetIndex];
  const enemyHere = fogContext ? fogContext.visibleEnemyIds.some(id => gameState.players.find(p => p.id === id)?.planetIndex === player.planetIndex) : false;
  const holdCover = t.coverHoldBonus > 0 && planetHere && !enemyHere ? t.coverHoldBonus * coverAround(planetHere, player.surfaceDir) : 0;
  const scores: Record<string, number> = {
    move: t.moveBase + t.moveMobility * personality.mobility + t.moveSpotted * spotted + t.moveMissingHealth * (1 - exposure.healthFraction),
    jump: t.jumpBase + t.jumpMobility * personality.mobility + t.jumpSpotted * spotted + t.jumpRisk * personality.riskTolerance + t.jumpPlanetDamage * exposure.planetDamage,
    skip: t.skipBase + t.skipStayPut * (1 - personality.mobility) - t.skipSpotted * spotted + holdCover,
  };

  // --- On-planet mode actions (plantStake, collect, deliver, pickup, etc.) ---
  if (skill >= t.objectiveMinSkill) {
    const modeScores = scoreModeActions(player, gameState, gameState.config.modeConfig, rng, fogContext, personality);
    for (const [action, score] of Object.entries(modeScores)) {
      if (score !== undefined && player.movementRemaining >= 0.5) {
        scores[action] = score;
      }
    }
  }

  // Same-planet objective: strongly boost 'move' when there's something to walk toward
  // (e.g., enemy core on this planet, collectible to grab)
  if (skill >= t.objectiveMinSkill && player.movementRemaining >= 0.5) {
    const samePlanetObj = getBotSamePlanetObjectiveDir(player, gameState);
    if (samePlanetObj) {
      scores.move += t.samePlanetObjectiveMoveBonus; // high priority — bot needs to reach the objective
    }
  }

  // --- Objective jump/move: boost when there's a mode target on another planet ---
  // Check this BEFORE shouldAlwaysMove so we can suppress move boost for carriers
  let hasUrgentOffPlanetObjective = false;
  if (skill >= t.objectiveMinSkill) {
    const objTarget = getBotObjectiveTarget(player, gameState);
    if (objTarget && objTarget.planetIndex !== player.planetIndex) {
      const carrying = getPlayerCarrying(player.id, gameState.carryables);
      const isCarrier = carrying && carrying.teamIndex !== player.teamIndex;
      if (isCarrier) {
        hasUrgentOffPlanetObjective = true;
        if (player.movementRemaining >= JUMP_MIN_COST) {
          scores.jump += t.carrierJumpBonus; // must get home — beats all other post-fire options
        }
        // Also boost move: walk toward home planet side (better jump angle next turn)
        scores.move += t.carrierMoveBonus;
      } else {
        // Not carrying — need to GET to the objective planet (e.g. enemy core in CTF)
        if (player.movementRemaining >= JUMP_MIN_COST) {
          scores.jump += objTarget.urgency;
        }
        // Walk toward objective planet side to improve jump angle next turn.
        // Strong boost: after firing, bot should always advance toward the objective.
        scores.move += t.objectiveMoveBonus;
      }
    }
  }

  // Hard/expert bots and defensive bots always reposition — but not when carrying an objective home
  const shouldAlwaysMove = (skill >= t.alwaysMoveMinSkill || personality.aggression < t.alwaysMoveMaxAggression) && !hasUrgentOffPlanetObjective;
  if (shouldAlwaysMove && player.movementRemaining >= 0.5) {
    scores.move += t.alwaysMoveBonus;
    scores.skip -= t.alwaysMoveSkipPenalty;
  }

  // Gate by movement remaining
  if (player.movementRemaining < 0.5) scores.move = -Infinity;
  if (player.movementRemaining < JUMP_MIN_COST) scores.jump = -Infinity;

  // Add noise based on inverse skill
  const noise = (1 - skill) * t.noise;
  for (const key of Object.keys(scores)) {
    if (scores[key] > -Infinity) {
      scores[key] += (rng.next() - 0.5) * noise;
    }
  }

  let best: BotPostFireAction = 'skip';
  let bestScore = -Infinity;
  for (const key of Object.keys(scores)) {
    if (scores[key] > bestScore) {
      bestScore = scores[key];
      best = key as BotPostFireAction;
    }
  }

  return best;
}
